(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) {
    root.FFCProductFinderMeasurement = api;
    api.bindGlobalLifecycle(root);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function deriveSurface(pathname) {
    if (pathname === '/') {
      return {
        page_surface: 'homepage_embedded',
        page_key: 'home',
        page_path: '/',
        template_key: 'index',
      };
    }
    if (pathname === '/pages/product-finder') {
      return {
        page_surface: 'standalone_page',
        page_key: 'product_finder',
        page_path: '/pages/product-finder',
        template_key: 'page.product-finder',
      };
    }
    return {
      page_surface: 'embedded_other',
      page_key: 'other',
      page_path: 'other',
      template_key: 'other',
    };
  }

  function cleanToken(value, fallback) {
    const cleaned = String(value || '').replace(/[^A-Za-z0-9_.:-]/g, '').slice(0, 100);
    return cleaned || fallback;
  }

  function randomId(win) {
    try {
      if (win.crypto && typeof win.crypto.randomUUID === 'function') return win.crypto.randomUUID();
    } catch (_) {}
    return `ffc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function createContext(root, win, idFactory) {
    const surface = deriveSurface(win && win.location ? win.location.pathname : '');
    const sectionId = cleanToken(root && root.dataset ? root.dataset.sectionId : '', 'unknown_section');
    const theme = win && win.Shopify && win.Shopify.theme ? win.Shopify.theme : {};
    const instanceId = cleanToken((idFactory || function () { return randomId(win || {}); })(), 'unknown_instance');
    const role = ['main', 'unpublished', 'development', 'demo'].includes(theme.role) ? theme.role : 'unknown';
    return Object.assign({}, surface, {
      finder_instance_id: instanceId,
      section_id: sectionId,
      content_group: `ffc_finder|${surface.page_surface}|${sectionId}`,
      theme_id: /^\d+$/.test(String(theme.id || '')) ? String(theme.id) : 'unknown',
      theme_role: role,
    });
  }

  const VERSION = 'native-v12-measurement';
  const MEASUREMENT_ID = 'G-T8MQDCZ3ZJ';
  const ATTRIBUTION_STORAGE_KEY = 'ffc-product-finder-attribution:v1';
  const ATTRIBUTION_TTL_SECONDS = 1800;
  const PENDING_INITIAL_EVENT_TTL_MS = 5000;
  const PROPERTY_KEYS = {
    _ffc_source: 'product_finder',
    _ffc_finder_version: VERSION,
  };
  const EVENT_FIELDS = {
    ffc_quiz_loaded: [],
    ffc_quiz_exposed: [],
    ffc_quiz_started: [],
    ffc_quiz_step_view: ['step_key', 'step_number', 'step_count'],
    ffc_quiz_step_completed: ['step_key', 'step_number', 'step_count'],
    ffc_quiz_results_rendered: ['recommendation_count', 'failure_count'],
    ffc_quiz_recommendation_rendered: ['product_handle', 'variant_id', 'recommendation_position'],
    ffc_quiz_recommendation_failed: ['product_handle', 'recommendation_position', 'failure_reason_class'],
    ffc_quiz_pdp_click: ['product_handle', 'variant_id', 'recommendation_position'],
    ffc_quiz_atc_attempt: ['product_handle', 'variant_id', 'attribution_route', 'attribution_token'],
    ffc_quiz_atc_verified: ['product_handle', 'variant_id', 'attribution_route', 'attribution_token'],
    ffc_quiz_atc_verification_failed: ['product_handle', 'variant_id', 'attribution_route', 'attribution_token', 'failure_reason_class'],
    ffc_quiz_add_to_cart_error: ['product_handle', 'variant_id', 'failure_reason_class'],
    ffc_quiz_validation_error: ['step_key'],
    ffc_quiz_submitted: ['recommendation_count'],
    ffc_quiz_back: ['from_step_key'],
    ffc_quiz_laminated_option_changed: ['product_handle', 'variant_id', 'laminated_card'],
    ffc_quiz_results_email_requested: ['product_handle'],
  };

  function analyticsState(win) {
    try {
      const privacy = win && win.Shopify && win.Shopify.customerPrivacy;
      const consentReady = Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function');
      return {
        consentReady,
        allowed: consentReady && privacy.analyticsProcessingAllowed() === true,
        gtagReady: Boolean(win && typeof win.gtag === 'function'),
      };
    } catch (_) {
      return { consentReady: false, allowed: false, gtagReady: false };
    }
  }

  function analyticsAllowed(win) {
    const state = analyticsState(win);
    return state.consentReady && state.allowed && state.gtagReady;
  }

  function isIntentionalClick(event) {
    return Boolean(event && event.isTrusted === true);
  }

  const INTEGER_FIELDS = {
    step_number: [1, 50],
    step_count: [1, 50],
    recommendation_count: [0, 10],
    failure_count: [0, 10],
    recommendation_position: [1, 10],
  };
  const FAILURE_REASONS = new Set([
    'attributed_line_not_found',
    'cart_readback_failed',
    'cart_request_failed',
    'exact_variant_unavailable',
    'product_lookup_failed',
    'unexpected_lookup_error',
  ]);

  function eventParameter(field, value) {
    if (Object.prototype.hasOwnProperty.call(INTEGER_FIELDS, field)) {
      const range = INTEGER_FIELDS[field];
      return Number.isInteger(value) && value >= range[0] && value <= range[1] ? value : undefined;
    }
    if (field === 'step_key' || field === 'from_step_key') {
      return typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,63}$/.test(value) ? value : undefined;
    }
    if (field === 'product_handle') return validHandle(value) ? String(value) : undefined;
    if (field === 'variant_id') return /^\d{1,20}$/.test(String(value || '')) ? String(value) : undefined;
    if (field === 'attribution_route') return ['direct_cart', 'pdp'].includes(value) ? value : undefined;
    if (field === 'attribution_token') {
      return typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,100}$/.test(value) ? value : undefined;
    }
    if (field === 'failure_reason_class') return FAILURE_REASONS.has(value) ? value : undefined;
    if (field === 'laminated_card') {
      return ['Digital Only', 'Digital + Laminated Sheet', 'No', 'Yes'].includes(value) ? value : undefined;
    }
    if (field === 'quiz_version') return value === VERSION ? value : undefined;
    return undefined;
  }

  function dedupeKey(name, detail) {
    const fields = {
      ffc_quiz_step_view: ['step_key'],
      ffc_quiz_step_completed: ['step_key'],
      ffc_quiz_recommendation_rendered: ['product_handle', 'variant_id'],
      ffc_quiz_recommendation_failed: ['product_handle', 'recommendation_position'],
      ffc_quiz_pdp_click: ['product_handle', 'variant_id'],
      ffc_quiz_atc_attempt: ['attribution_token', 'product_handle', 'variant_id'],
      ffc_quiz_atc_verified: ['attribution_token'],
      ffc_quiz_atc_verification_failed: ['attribution_token'],
      ffc_quiz_add_to_cart_error: ['product_handle', 'variant_id'],
      ffc_quiz_validation_error: ['step_key'],
      ffc_quiz_laminated_option_changed: ['product_handle', 'variant_id', 'laminated_card'],
      ffc_quiz_results_email_requested: ['product_handle'],
    }[name] || [];
    return [name].concat(fields.map(function (field) { return eventParameter(field, detail && detail[field]) || ''; })).join('|');
  }

  function isQueueableInitialEvent(name, detail) {
    if (name === 'ffc_quiz_loaded') return true;
    return name === 'ffc_quiz_step_view' && eventParameter('step_number', detail && detail.step_number) === 1;
  }

  function createTracker(root, win, idFactory, suppliedContext) {
    const context = suppliedContext || createContext(root, win, idFactory);
    const sent = new Set();
    let sequence = 0;

    function transmit(name, detail) {
      if (!Object.prototype.hasOwnProperty.call(EVENT_FIELDS, name)) return false;
      if (!analyticsAllowed(win) || !win || typeof win.gtag !== 'function') return false;
      const key = dedupeKey(name, detail || {});
      if (sent.has(key)) return false;
      const nextSequence = sequence + 1;
      const params = Object.assign({}, context, {
        event_id: `${context.finder_instance_id}:${nextSequence}`,
        event_seq: nextSequence,
        send_to: MEASUREMENT_ID,
        transport_type: 'beacon',
      });
      EVENT_FIELDS[name].forEach(function (field) {
        const value = eventParameter(field, detail && detail[field]);
        if (value !== undefined && value !== '') params[field] = value;
      });
      const version = eventParameter('quiz_version', detail && detail.quiz_version);
      if (version) params.quiz_version = version;
      try {
        win.gtag('event', name, params);
      } catch (_) {
        return false;
      }
      sent.add(key);
      sequence = nextSequence;
      return true;
    }

    return { context, transmit };
  }

  function dispatch(root, win, name, detail) {
    try {
      root.dispatchEvent(new win.CustomEvent(name, { bubbles: true, detail: detail || {} }));
    } catch (_) {}
  }

  function buildAttribution(context, handle, variantId, route, token, createdAt) {
    if (!validHandle(handle) || !/^\d+$/.test(String(variantId || ''))) return null;
    if (!['direct_cart', 'pdp'].includes(route)) return null;
    const cleanAttributionToken = cleanToken(token, '');
    if (!context || !cleanAttributionToken || !Number.isInteger(createdAt)) return null;
    return Object.assign({}, context, {
      source: 'product_finder',
      version: VERSION,
      product_handle: handle,
      variant_id: String(variantId),
      attribution_token: cleanAttributionToken,
      attribution_route: route,
      created_at: createdAt,
      expires_at: createdAt + ATTRIBUTION_TTL_SECONDS,
    });
  }

  function attributionProperties(record) {
    return Object.assign({}, PROPERTY_KEYS, {
      _ffc_recommendation: record.product_handle,
      _ffc_finder_surface: record.page_surface,
      _ffc_finder_section: record.section_id,
      _ffc_finder_instance: record.finder_instance_id,
      _ffc_attribution_token: record.attribution_token,
      _ffc_attribution_route: record.attribution_route,
      _ffc_attribution_ts: String(record.created_at),
    });
  }

  function contextFromAttribution(record) {
    return {
      finder_instance_id: record.finder_instance_id,
      section_id: record.section_id,
      page_surface: record.page_surface,
      page_key: record.page_key,
      page_path: record.page_path,
      template_key: record.template_key,
      content_group: `ffc_finder|${record.page_surface}|${record.section_id}`,
      theme_id: record.theme_id,
      theme_role: record.theme_role,
    };
  }

  function matchCartLine(cart, record) {
    if (!cart || !Array.isArray(cart.items) || !record) return false;
    const expectedProperties = attributionProperties(record);
    const expectedQuantity = Number.isInteger(record.expected_quantity) && record.expected_quantity > 0
      ? record.expected_quantity
      : 1;
    const matching = cart.items.filter(function (item) {
      const properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
      return String(item && item.id || '') === String(record.variant_id) &&
        Object.keys(expectedProperties).every(function (key) {
          return properties[key] === expectedProperties[key];
        });
    });
    return matching.length === 1 && Number(matching[0].quantity) === expectedQuantity;
  }

  function validHandle(value) {
    return /^[a-z0-9][a-z0-9-]{0,99}$/.test(String(value || ''));
  }

  function validateAttribution(raw, now) {
    if (!raw || raw.source !== 'product_finder' || raw.version !== VERSION) return null;
    if (!validHandle(raw.product_handle) || !/^\d+$/.test(String(raw.variant_id || ''))) return null;
    if (!['homepage_embedded', 'standalone_page', 'embedded_other'].includes(raw.page_surface)) return null;
    if (raw.attribution_route !== 'pdp') return null;
    if (!Number.isInteger(raw.created_at) || !Number.isInteger(raw.expires_at) || raw.created_at > now + 60) return null;
    const fixedExpiry = raw.created_at + ATTRIBUTION_TTL_SECONDS;
    if (raw.expires_at !== fixedExpiry || now >= fixedExpiry) return null;
    const sectionId = cleanToken(raw.section_id, '');
    const instanceId = cleanToken(raw.finder_instance_id, '');
    const token = cleanToken(raw.attribution_token, '');
    if (!sectionId || !instanceId || !token) return null;
    return {
      source: 'product_finder',
      version: VERSION,
      product_handle: raw.product_handle,
      variant_id: String(raw.variant_id),
      page_surface: raw.page_surface,
      page_key: ['home', 'product_finder', 'other'].includes(raw.page_key) ? raw.page_key : 'other',
      page_path: raw.page_path === '/' || raw.page_path === '/pages/product-finder' ? raw.page_path : 'other',
      template_key: ['index', 'page.product-finder', 'other'].includes(raw.template_key) ? raw.template_key : 'other',
      section_id: sectionId,
      finder_instance_id: instanceId,
      attribution_token: token,
      attribution_route: 'pdp',
      created_at: raw.created_at,
      expires_at: raw.expires_at,
      theme_id: /^\d+$/.test(String(raw.theme_id || '')) ? String(raw.theme_id) : 'unknown',
      theme_role: ['main', 'unpublished', 'development', 'demo'].includes(raw.theme_role) ? raw.theme_role : 'unknown',
    };
  }

  function productHandleFromPath(pathname) {
    const match = String(pathname || '').match(/^\/products\/([a-z0-9][a-z0-9-]{0,99})\/?$/);
    return match ? match[1] : '';
  }

  function readStoredAttribution(win, now) {
    let raw = null;
    try { raw = JSON.parse(win.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || 'null'); } catch (_) {}
    const record = validateAttribution(raw, now);
    if (!record && raw) {
      try { win.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY); } catch (_) {}
    }
    return record;
  }

  function applyAttributionToForms(document, record) {
    if (!record || productHandleFromPath(document.location.pathname) !== record.product_handle) return 0;
    const properties = attributionProperties(record);
    let applied = 0;
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      Object.keys(properties).forEach(function (key) {
        const name = `properties[${key}]`;
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.dataset.ffcFinderAttribution = 'true';
          form.appendChild(input);
        }
        input.value = properties[key];
      });
      applied += 1;
    });
    return applied;
  }

  function install(win, options) {
    const opts = options || {};
    const document = win && win.document;
    const trackerByRoot = new WeakMap();
    const pendingByRoot = new WeakMap();
    const directRecordsByRoot = new WeakMap();
    const pendingPromises = new Set();
    const records = [];
    const nowSeconds = function () {
      return typeof opts.nowSeconds === 'function' ? Math.floor(opts.nowSeconds()) : Math.floor(Date.now() / 1000);
    };
    const nowMillis = function () {
      return typeof opts.nowMillis === 'function' ? Math.floor(opts.nowMillis()) : Date.now();
    };
    if (!document) return { destroy: function () {}, getTracker: function () { return null; }, mount: function () {} };
    let activePdpRecord = readStoredAttribution(win, nowSeconds());
    let pdpTracker = activePdpRecord
      ? createTracker(null, win, null, contextFromAttribution(activePdpRecord))
      : null;

    function mountRoot(root) {
      if (!root || trackerByRoot.has(root)) return trackerByRoot.get(root) || null;
      const tracker = createTracker(root, win, opts.idFactory);
      trackerByRoot.set(root, tracker);
      const cleanups = [];
      const pendingEvents = new Map();
      let pendingOrder = 0;
      let retryTimer = null;
      let retryAttempts = 0;

      function clearPendingEvents() {
        pendingEvents.clear();
        if (retryTimer !== null && typeof win.clearTimeout === 'function') {
          win.clearTimeout(retryTimer);
          retryTimer = null;
        }
      }

      function removeExpiredPendingEvents() {
        const currentTime = nowMillis();
        pendingEvents.forEach(function (item, key) {
          if (currentTime >= item.expiresAtMillis) pendingEvents.delete(key);
        });
      }

      function schedulePendingFlush() {
        if (retryTimer !== null || retryAttempts >= 50 || typeof win.setTimeout !== 'function') return;
        retryAttempts += 1;
        retryTimer = win.setTimeout(function () {
          retryTimer = null;
          flushPendingEvents();
        }, 100);
      }

      function flushPendingEvents() {
        removeExpiredPendingEvents();
        if (!pendingEvents.size) return;
        const state = analyticsState(win);
        if (state.consentReady && !state.allowed) {
          clearPendingEvents();
          return;
        }
        if (!state.consentReady || !state.gtagReady) {
          schedulePendingFlush();
          return;
        }
        const queued = Array.from(pendingEvents.values()).sort(function (left, right) {
          const leftPriority = left.name === 'ffc_quiz_loaded' ? 0 : left.name === 'ffc_quiz_step_view' ? 1 : 2;
          const rightPriority = right.name === 'ffc_quiz_loaded' ? 0 : right.name === 'ffc_quiz_step_view' ? 1 : 2;
          return leftPriority - rightPriority || left.order - right.order;
        });
        pendingEvents.clear();
        queued.forEach(function (item) { tracker.transmit(item.name, item.detail); });
      }

      function transmitOrQueue(name, detail) {
        const state = analyticsState(win);
        if (state.consentReady && !state.allowed) {
          clearPendingEvents();
          return false;
        }
        if (!state.consentReady || !state.gtagReady) {
          if (!isQueueableInitialEvent(name, detail || {})) return false;
          const key = dedupeKey(name, detail || {});
          if (!pendingEvents.has(key)) {
            pendingOrder += 1;
            pendingEvents.set(key, {
              name,
              detail: detail || {},
              order: pendingOrder,
              expiresAtMillis: nowMillis() + PENDING_INITIAL_EVENT_TTL_MS,
            });
          }
          schedulePendingFlush();
          return false;
        }
        return tracker.transmit(name, detail);
      }

      document.addEventListener('visitorConsentCollected', flushPendingEvents);
      win.addEventListener('load', flushPendingEvents);
      cleanups.push(function () {
        document.removeEventListener('visitorConsentCollected', flushPendingEvents);
        win.removeEventListener('load', flushPendingEvents);
        clearPendingEvents();
      });
      Object.keys(EVENT_FIELDS).forEach(function (name) {
        const handler = function (event) { transmitOrQueue(name, event.detail || {}); };
        root.addEventListener(name, handler);
        cleanups.push(function () { root.removeEventListener(name, handler); });
      });

      const form = root.querySelector('[data-ffc-finder-form]');
      let started = false;
      if (form) {
        const onChange = function (event) {
          const intentional = typeof opts.isIntentionalEvent === 'function'
            ? opts.isIntentionalEvent(event)
            : event.isTrusted === true;
          if (started || !intentional) return;
          started = true;
          dispatch(root, win, 'ffc_quiz_started', { quiz_version: 'native-v12-measurement' });
        };
        form.addEventListener('change', onChange, true);
        cleanups.push(function () { form.removeEventListener('change', onChange, true); });
      }

      let exposed = false;
      let observer = null;
      const onEntries = function (entries) {
        if (exposed) return;
        const visible = entries.some(function (entry) {
          return entry.target === root && entry.isIntersecting === true && Number(entry.intersectionRatio || 0) >= 0.1;
        });
        if (!visible) return;
        exposed = true;
        if (observer && typeof observer.disconnect === 'function') observer.disconnect();
        dispatch(root, win, 'ffc_quiz_exposed', { quiz_version: 'native-v12-measurement' });
      };
      try {
        observer = typeof opts.observerFactory === 'function'
          ? opts.observerFactory(onEntries)
          : new win.IntersectionObserver(onEntries, { threshold: [0.1] });
        observer.observe(root);
        cleanups.push(function () { if (observer && typeof observer.disconnect === 'function') observer.disconnect(); });
      } catch (_) {}

      records.push({ root, cleanups });
      return tracker;
    }

    function mount(scope) {
      if (!scope) return;
      if (scope.matches && scope.matches('.ffc-product-finder')) mountRoot(scope);
      if (scope.querySelectorAll) scope.querySelectorAll('.ffc-product-finder').forEach(mountRoot);
    }

    function directCartProperties(root, handle, variantId) {
      const tracker = trackerByRoot.get(root);
      if (!tracker) return Object.assign({}, PROPERTY_KEYS, { _ffc_recommendation: handle });
      let records = directRecordsByRoot.get(root);
      if (!records) {
        records = new Map();
        directRecordsByRoot.set(root, records);
      }
      const key = `${handle}|${String(variantId || '')}`;
      let record = records.get(key);
      if (!record) {
        const token = cleanToken((opts.tokenFactory || function () { return randomId(win); })(), '');
        record = buildAttribution(tracker.context, handle, variantId, 'direct_cart', token, nowSeconds());
        if (!record) return Object.assign({}, PROPERTY_KEYS, { _ffc_recommendation: handle });
        records.set(key, record);
      }
      pendingByRoot.set(root, record);
      return attributionProperties(record);
    }

    function emitForAttribution(root, tracker, name, record, extra) {
      const detail = Object.assign({
        quiz_version: VERSION,
        product_handle: record.product_handle,
        variant_id: record.variant_id,
        attribution_route: record.attribution_route,
        attribution_token: record.attribution_token,
      }, extra || {});
      if (root) dispatch(root, win, name, detail);
      else if (tracker) tracker.transmit(name, detail);
    }

    function clearPdpAttribution() {
      try { win.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY); } catch (_) {}
      document.querySelectorAll('[data-ffc-finder-attribution]').forEach(function (input) { input.remove(); });
      activePdpRecord = null;
      pdpTracker = null;
    }

    async function verifyRecord(root, tracker, record) {
      let failureReason = 'cart_readback_failed';
      try {
        const fetchImpl = opts.fetchImpl || win.fetch.bind(win);
        const response = await fetchImpl('/cart.js', {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response || !response.ok) throw new Error('cart_readback_failed');
        const cart = await response.json();
        if (!matchCartLine(cart, record)) {
          failureReason = 'attributed_line_not_found';
          throw new Error(failureReason);
        }
        emitForAttribution(root, tracker, 'ffc_quiz_atc_verified', record);
        if (root) pendingByRoot.delete(root);
        else clearPdpAttribution();
        return true;
      } catch (_) {
        emitForAttribution(root, tracker, 'ffc_quiz_atc_verification_failed', record, { failure_reason_class: failureReason });
        return false;
      }
    }

    function trackPromise(promise) {
      pendingPromises.add(promise);
      promise.finally(function () { pendingPromises.delete(promise); });
      return promise;
    }

    const onAjaxAdded = function (event) {
      const root = event.target && event.target.closest ? event.target.closest('.ffc-product-finder') : null;
      const tracker = root ? trackerByRoot.get(root) : null;
      const record = root ? pendingByRoot.get(root) : activePdpRecord;
      const effectiveTracker = root ? tracker : pdpTracker;
      if (!root && (!record || !Number.isInteger(record.attempted_at))) return;
      if (!record || !effectiveTracker) return;
      if (root) record.expected_quantity = (record.expected_quantity || 0) + 1;
      trackPromise(verifyRecord(root, effectiveTracker, record));
    };

    const onPdpSubmit = function (event) {
      const form = event.target && event.target.matches && event.target.matches('form[action*="/cart/add"]') ? event.target : null;
      if (!form || !activePdpRecord || !pdpTracker) return;
      const refreshed = validateAttribution(activePdpRecord, nowSeconds());
      const variantInput = form.querySelector('[name="id"]');
      if (!refreshed || !variantInput || String(variantInput.value) !== activePdpRecord.variant_id) {
        form.querySelectorAll('[data-ffc-finder-attribution]').forEach(function (input) { input.remove(); });
        if (!refreshed) clearPdpAttribution();
        return;
      }
      applyAttributionToForms(document, refreshed);
      activePdpRecord.attempted_at = nowSeconds();
      try { win.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(activePdpRecord)); } catch (_) {}
      emitForAttribution(null, pdpTracker, 'ffc_quiz_atc_attempt', activePdpRecord);
    };

    const onSectionLoad = function (event) { mount(event.target); };
    const onPdpClick = function (event) {
      const link = event.target && event.target.closest ? event.target.closest('[data-ffc-pdp]') : null;
      if (!link) return;
      const intentional = typeof opts.isIntentionalClick === 'function'
        ? opts.isIntentionalClick(event)
        : isIntentionalClick(event);
      if (!intentional) return;
      const root = link.closest('.ffc-product-finder');
      const card = link.closest('[data-product-handle]');
      const tracker = root ? trackerByRoot.get(root) : null;
      const handle = card && validHandle(card.dataset.productHandle) ? card.dataset.productHandle : '';
      const variantId = String(link.dataset.variantId || '');
      if (!tracker || !handle || !/^\d+$/.test(variantId)) return;
      const createdAt = nowSeconds();
      const token = cleanToken((opts.tokenFactory || function () { return randomId(win); })(), '');
      if (!token) return;
      const record = Object.assign({}, tracker.context, {
        source: 'product_finder',
        version: VERSION,
        product_handle: handle,
        variant_id: variantId,
        attribution_token: token,
        attribution_route: 'pdp',
        created_at: createdAt,
        expires_at: createdAt + ATTRIBUTION_TTL_SECONDS,
      });
      try { win.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record)); } catch (_) {}
    };
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('click', onPdpClick, true);
    document.addEventListener('submit', onPdpSubmit, true);
    document.addEventListener('ajaxProduct:added', onAjaxAdded);
    mount(document);
    applyAttributionToForms(document, activePdpRecord);

    function destroy() {
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('click', onPdpClick, true);
      document.removeEventListener('submit', onPdpSubmit, true);
      document.removeEventListener('ajaxProduct:added', onAjaxAdded);
      records.forEach(function (record) { record.cleanups.forEach(function (cleanup) { cleanup(); }); });
    }

    async function flush() {
      while (pendingPromises.size) await Promise.allSettled(Array.from(pendingPromises));
    }

    return {
      destroy,
      directCartProperties,
      flush,
      getTracker: function (root) { return trackerByRoot.get(root) || null; },
      mount,
    };
  }

  function bindGlobalLifecycle(win) {
    if (!win || !win.document || win.__ffcProductFinderMeasurementLifecycleBound) {
      return win && win.__ffcProductFinderMeasurementInstallation || null;
    }
    win.__ffcProductFinderMeasurementLifecycleBound = true;
    const start = function () {
      if (!win.__ffcProductFinderMeasurementInstallation) {
        win.__ffcProductFinderMeasurementInstallation = install(win);
      }
      return win.__ffcProductFinderMeasurementInstallation;
    };
    if (win.document.readyState === 'loading') {
      win.document.addEventListener('DOMContentLoaded', start, { once: true });
      return null;
    }
    return start();
  }

  return {
    applyAttributionToForms,
    attributionProperties,
    bindGlobalLifecycle,
    buildAttribution,
    createContext,
    createTracker,
    deriveSurface,
    install,
    isIntentionalClick,
    matchCartLine,
    readStoredAttribution,
  };
});
